import { cloneElement } from 'react';

import {
  comboboxClear,
  comboboxDivider,
  comboboxOptions,
  dropdown,
} from '@mage-ui/styled-system/recipes';
import { Combobox as MantineCombobox, useCombobox } from '@mantine/core';
import { ButtonIcon } from '@/components/buttons';

export type DropdownBaseProps = {
  defaultOpened?: boolean;
  readOnly?: boolean;
  setQuery?: (value: string) => void;
  selectedOption?: { id: string | number; value: string } | undefined;
  setSelectedOption: (value: string | undefined) => void;
  target: React.ReactElement<{
    startSlot?: React.ReactNode;
    endSlot?: React.ReactNode;
    onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
    onBlur?: () => void;
    onClick?: () => void;
  }>;
  classNames?: {
    dropdown?: string;
    options?: string;
    divider?: string;
  };
  endSlot?: React.ReactNode;
  startSlot?: React.ReactNode;
  isClearable?: boolean;
  clearableIcon?: string;
  children: React.ReactNode;
  portal?: boolean;
  position?:
    | 'bottom'
    | 'bottom-start'
    | 'bottom-end'
    | 'top'
    | 'top-start'
    | 'top-end'
    | 'right'
    | 'right-start'
    | 'right-end'
    | 'left'
    | 'left-start'
    | 'left-end';
  width?: number | string;
  keepMounted?: boolean;
};

export const DropdownBase = ({
  defaultOpened,
  readOnly,
  setQuery,
  setSelectedOption,
  selectedOption,
  target,
  classNames,
  children,
  endSlot,
  startSlot,
  position,
  portal,
  width,
  isClearable,
  clearableIcon,
  ...props
}: DropdownBaseProps) => {
  const combobox = useCombobox({
    defaultOpened: defaultOpened ?? false,
    scrollBehavior: 'smooth',
    onDropdownClose: () => {
      combobox.resetSelectedOption();
    },
  });

  const handleClear = () => {
    setQuery?.('');
    setSelectedOption(undefined);
    combobox.resetSelectedOption();
  };
  const showClearButton = isClearable && (selectedOption?.value || '');

  const clearButton = (
    <ButtonIcon
      classNames={{
        root: comboboxClear(),
      }}
      icon={clearableIcon || 'x-close'}
      label='clear input'
      onClick={(e) => {
        e.stopPropagation();
        handleClear();
      }}
    />
  );

  const targetComponent = cloneElement(target, {
    ...(target?.props ?? {}),
    onChange: (event: React.ChangeEvent<HTMLInputElement>) => {
      target?.props?.onChange?.(event);
      setQuery?.(event.currentTarget.value);
      combobox.openDropdown();
      combobox.updateSelectedOptionIndex();
    },
    onBlur: () => {
      setQuery?.(selectedOption?.value ?? '');
    },
    onClick: () => {
      if (readOnly) {
        combobox.toggleDropdown();
      } else {
        combobox.openDropdown();
      }
    },
    // opened: combobox.isDropdownOpened,
    startSlot: startSlot,
    endSlot: showClearButton ? clearButton : endSlot,
  });

  return (
    <MantineCombobox
      readOnly={readOnly ?? false}
      onOptionSubmit={(option) => {
        setSelectedOption(option);
        setQuery?.(option);
        combobox.closeDropdown();
      }}
      store={combobox}
      withinPortal={portal || true}
      position={position || 'bottom-start'}
      width={width || 'auto'}
      {...props}
    >
      <MantineCombobox.Target>{targetComponent}</MantineCombobox.Target>
      <MantineCombobox.Dropdown
        classNames={{
          dropdown: classNames?.dropdown ?? dropdown(),
        }}
      >
        <MantineCombobox.Options
          classNames={{
            options: classNames?.options ?? comboboxOptions(),
          }}
        >
          {children}
        </MantineCombobox.Options>
      </MantineCombobox.Dropdown>
    </MantineCombobox>
  );
};

const Divider: React.FC<{ classNames?: { divider?: string } }> = ({
  classNames,
}) => {
  return (
    <hr
      className={classNames?.divider ?? comboboxDivider()}
      aria-orientation='horizontal'
    />
  );
};

DropdownBase.Option = MantineCombobox.Option;
DropdownBase.Option.displayName = 'DropdownBase.Option';
DropdownBase.Divider = Divider;
DropdownBase.Divider.displayName = 'DropdownBase.Divider';
DropdownBase.Empty = MantineCombobox.Empty;
DropdownBase.Empty.displayName = 'DropdownBase.Empty';
